import React from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import { Container, Draggable } from '../../../Common/Lib/DragAndDrop'
import { Box, Cursor, MarginBottom, MarginLeft } from '../../../Common/Components/Styles'
import { FormControl } from '../Forms'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dropdown from '../../../Common/Components/Dropdown'
import NumberTextField from '../../../Common/Components/NumberTextField'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import DragIcon from '@mui/icons-material/DragIndicator'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import * as R from 'ramda'
import { setStateFromEvent } from '../../Utils/Events'

const Row = styled.div`
  align-items: center;
  border: 1px solid ${(props) => props.bg};
  border-top: ${({ first, bg }) => first ? '1px solid ' + bg : '0px solid'};
  display: flex;
  justify-content: space-between;
  min-width: 300px;
  padding: 0;
`

const SettingsDialog = ({
  open,
  handleClose,
  handleReset,
  handleSaveAndClose,
  columns,
  onSelect,
  onSort,
  listPerPage,
  onListPerPageChange,
  defaultSortField,
  defaultSortDirection,
  onDefaultSortFieldChange,
  onDefaultSortDirectionChange,
  disableSorting,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [itemsPerPage, setItemsPerPage] = React.useState(listPerPage)
  const selected = columns.filter(R.prop('visible')).map(R.prop('id'))

  const handleDrop = ({ removedIndex, addedIndex }) => {
    const copy = [...columns]
    onSort(
      R.ifElse(
        R.isNil,
        R.always(columns),
        R.compose(
          R.always(copy),
          R.always(copy.splice(addedIndex, 0, copy.splice(removedIndex, 1)[0])),
        ),
        R.always(columns),
      )(removedIndex),
    )
  }

  const sortedColumns = columns.sort((a, b) => {
    if (a.visible && !b.visible) {
      return -1
    } else if (b.visible && !a.visible) {
      return 1
    } else {
      return 0
    }
  })

  const handleChangeItemsPerPage = (event) => {
    const value = event.target.value
    setItemsPerPage(value)
    if (value) {
      onListPerPageChange(Math.max(1, value))
    }
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{t('common:generic.Settings')}</DialogTitle>
      <DialogContent>
        <FormControl compact>
          <NumberTextField
            required
            min={1}
            max={100}
            label={t('common:ui.ItemsPerPage')}
            helperText={t('common:ui.ChangelistPageItemsLimit')}
            value={itemsPerPage}
            onChange={handleChangeItemsPerPage}
          />
        </FormControl>
        <FormControl compact>
          <Box direction='row' margin='0 0 1rem'>
            <Dropdown
              label={t('common:ui.DefaultSortField')}
              required
              value={defaultSortField.replace(/\//g, ".")}
              onChange={setStateFromEvent(onDefaultSortFieldChange)}
              options={
                columns
                  .filter(c => disableSorting.indexOf(c.id) === -1)
                  .map((c) => ({ value: c.id, label: c.label }))

              }
            />
            <MarginLeft value='1rem'>
              <Dropdown
                label={t('common:ui.DefaultSortDirection')}
                required
                value={defaultSortDirection}
                onChange={setStateFromEvent(onDefaultSortDirectionChange)}
                options={[
                  { value: 'asc', label: t('common:ui.ASC') },
                  { value: 'desc', label: t('common:ui.DESC') },
                ]}
                width='100px'
              />
            </MarginLeft>
          </Box>
        </FormControl>
        <MarginBottom value="1.5rem">
          <DialogContentText>
            {t('common:ui:ColumnsSettings')}
          </DialogContentText>
        </MarginBottom>
        <Container onDrop={handleDrop}>
          {sortedColumns.map((column, idx) => {
            return (
              <Draggable key={`row-${column.id}`}>
                <Row bg={theme.palette.contrastLight.main} first={idx === 0}>
                  <div>
                    <Checkbox
                      checked={selected.indexOf(column.id) !== -1}
                      onChange={onSelect(column.id)}
                    />
                    <span>{column.label}</span>
                  </div>
                  <Cursor name="move">
                    <DragIcon />
                  </Cursor>
                </Row>
              </Draggable>
            )
          })}
        </Container>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset} color="secondary">
          {t('common:buttons.Reset')}
        </Button>
        <Button onClick={handleClose} color="primary">
          {t('common:buttons.Close')}
        </Button>
        <Button onClick={handleSaveAndClose} color="primary">
          {t('common:buttons.SaveAndClose')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

SettingsDialog.defaultProps = {
  disableSorting: [],
}

SettingsDialog.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      visible: PropTypes.bool,
    }),
  ),
  open: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  handleReset: PropTypes.func.isRequired,
  handleSaveAndClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  listPerPage: PropTypes.number.isRequired,
  onListPerPageChange: PropTypes.func.isRequired,
  defaultSortField: PropTypes.string,
  defaultSortDirection: PropTypes.string,
  onDefaultSortFieldChange: PropTypes.func,
  onDefaultSortDirectionChange: PropTypes.func,
  disableSorting: PropTypes.array,
  sortMapping: PropTypes.object,
}

export default SettingsDialog
